import { displayText } from '../../../terminology/store';
import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import ThemeContext from '../../ThemeContext';

axios.defaults.baseURL = `${process.env.REACT_APP_BACKEND_URL}`;

const Notify = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);

  // Typography from theme
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;
  const titleFontSize = theme.typography?.titleFontSize || 24;

  const [users, setUsers] = useState([]);
  const [includeClientAdmins, setIncludeClientAdmins] = useState(false);
  const [includeAllUsers, setIncludeAllUsers] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setApiError('No authentication token found. Please log in.');
          return;
        }

        const params = {};
        if (includeAllUsers) {
          params.include_all_users = true;
        } else if (includeClientAdmins) {
          params.include_client_admins = true;
        }

        const response = await axios.get(
          '/api/webmanager/mass-mail/recipients',
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params,
          }
        );

        console.log('API Response:', response.data);
        setUsers(response.data);
        setApiError(null);
      } catch (error) {
        console.error('Error fetching users:', {
          message: error.message,
          response: error.response
            ? {
                status: error.response.status,
                data: error.response.data,
              }
            : null,
        });
        setApiError(
          error.response?.status === 403
            ? 'Unauthorized. Only super admins can access recipients.'
            : error.response?.status === 404
            ? 'Recipients endpoint not found. Check the API URL.'
            : 'Failed to fetch recipients. Please try again later.'
        );
      }
    };
    fetchUsers();
  }, [includeClientAdmins, includeAllUsers]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortValue = (user, field) => {
    if (field === 'id') return user.id;
    if (field === 'reseller_since')
      return user.reseller_since ? new Date(user.reseller_since) : null;
    let val;
    switch (field) {
      case 'name':
        val = user.name || 'N/A';
        break;
      case 'role':
        val = user.role || 'N/A';
        break;
      case 'city':
        val = user.city || 'N/A';
        break;
      case 'state':
        val = user.state || 'N/A';
        break;
      case 'primary_contact':
        val = user.primary_contact || 'N/A';
        break;
      default:
        val = user[field] || 'N/A';
    }
    return typeof val === 'string' ? val.toLowerCase() : val;
  };

  const sortedUsers = [...users].sort((a, b) => {
    const valA = getSortValue(a, sortField);
    const valB = getSortValue(b, sortField);
    let ascCmp;
    if (valA instanceof Date && valB instanceof Date) {
      ascCmp = valA.getTime() - valB.getTime();
    } else if (valA === null && valB === null) {
      ascCmp = 0;
    } else if (valA === null) {
      ascCmp = 1;
    } else if (valB === null) {
      ascCmp = -1;
    } else {
      ascCmp = valA < valB ? -1 : valA > valB ? 1 : 0;
    }
    return sortOrder === 'asc' ? ascCmp : -ascCmp;
  });

  const handleExportEmailList = () => {
    if (!sortedUsers.length) {
      toast.info('No users to export.');
      return;
    }

    const data = sortedUsers.map((user) => ({
      ID: user.id,
      Name: user.name,
      Role: user.role,
      'Reseller Since': user.reseller_since
        ? new Date(user.reseller_since).toLocaleDateString()
        : '',
      City: user.city || 'N/A',
      State: user.state || 'N/A',
      'Primary Contact': user.primary_contact || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    XLSX.writeFile(workbook, 'User_Email_List.xlsx');
  };

  const handlePrepareMessage = () => {
    setShowPopup(true);
  };

  const handleSendMessage = async () => {
    const newErrors = {};
    if (!subject) newErrors.subject = 'Subject is required';
    if (!message) newErrors.message = 'Message is required';
    if (selectedUsers.length === 0)
      newErrors.users = 'At least one user must be selected';
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSending(true);
      const selectedUserEmails = sortedUsers
        .filter((user) => selectedUsers.includes(user.id))
        .map((user) => user.email);

      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          '/api/webmanager/mass-mail/send',
          {
            recipients: selectedUserEmails,
            subject,
            message,
            send_type: 'multiple',
            include_client_admins: includeClientAdmins,
            include_all_users: includeAllUsers,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(response.data.success || 'Emails sent successfully.');
        setShowPopup(false);
        setSubject('');
        setMessage('');
        setErrors({});
        setSelectedUsers([]);
      } catch (error) {
        console.error(
          'Error sending mass email:',
          error.response?.data || error.message
        );
        toast.error(
          error.response?.data?.error || 'Failed to send emails. Please try again.'
        );
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleCancelPopup = () => {
    setShowPopup(false);
    setSubject('');
    setMessage('');
    setErrors({});
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === sortedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(sortedUsers.map((user) => user.id));
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px` }}>
      {/* WHITE CARD CONTAINER - Same as other pages */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "1rem",
          borderRadius: "10px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          fontFamily,
          marginTop: "10px",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            gap: '0.5rem',
          }}
        >
          <h4
            style={{
              fontSize: `${titleFontSize}px`,
              fontWeight: 'bold',
              color: theme.colors.textPrimary,
              marginBottom: 0,
              textAlign: 'left',
              fontFamily,
            }}
          >
            Send Mass Notifications
          </h4>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5rem',
              justifyContent: window.innerWidth < 600 ? 'stretch' : 'flex-end',
            }}
          >
            <Link
              to="/superadmin/notify/logs"
              style={{
                background: theme.gradients?.primary || theme.colors.primary,
                color: theme.colors.textWhite,
                padding: "6px 12px",
                borderRadius: "8px",
                fontSize: "clamp(10px, 2.5vw, 11px)",
                textDecoration: "none",
                minWidth: "120px",
                textAlign: "center",
                fontFamily,
              }}
            >
              View Logs
            </Link>
          </div>
        </div>

        {/* OPTIONS BAR */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            justifyContent: 'space-between',
            borderRadius: '12px',
            background: theme.gradients?.secondary || theme.colors.secondary,
            color: theme.colors.textWhite,
            gap: '0.5rem',
            padding: '0.8rem 1rem',
            marginBottom: '1rem',
            fontFamily,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            <span
              style={{
                fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                fontWeight: '500',
              }}
            >{displayText(" For selected Resellers: ")}</span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="includeClientAdmins"
                checked={includeClientAdmins}
                onChange={() => {
                  setIncludeClientAdmins(!includeClientAdmins);
                  if (includeAllUsers) setIncludeAllUsers(false);
                }}
                style={{
                  cursor: 'pointer',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  border: `2px solid ${theme.colors.textWhite}`,
                  backgroundColor: includeClientAdmins ? '#ffffff' : 'transparent',
                  appearance: 'none',
                }}
              />
              <label
                htmlFor="includeClientAdmins"
                style={{
                  fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                  cursor: 'pointer',
                }}
              >{displayText(" Include Client Admins ")}</label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="includeAllUsers"
                checked={includeAllUsers}
                onChange={() => {
                  setIncludeAllUsers(!includeAllUsers);
                  if (includeClientAdmins) setIncludeClientAdmins(false);
                }}
                style={{
                  cursor: 'pointer',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  border: `2px solid ${theme.colors.textWhite}`,
                  backgroundColor: includeAllUsers ? '#ffffff' : 'transparent',
                  appearance: 'none',
                }}
              />
              <label
                htmlFor="includeAllUsers"
                style={{
                  fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                  cursor: 'pointer',
                }}
              >
                Include All Users
              </label>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: window.innerWidth < 600 ? 'stretch' : 'flex-end',
            }}
          >
            <button
              onClick={handleExportEmailList}
              style={{
                background: theme.gradients?.primary || theme.colors.primary,
                color: theme.colors.textWhite,
                padding: "6px 12px",
                borderRadius: "8px",
                fontSize: "clamp(10px, 2.5vw, 11px)",
                border: "none",
                minWidth: "130px",
                fontFamily,
              }}
            >
              Export Email List
            </button>

            <button
              onClick={handlePrepareMessage}
              style={{
                background: theme.gradients?.primary || theme.colors.primary,
                color: theme.colors.textWhite,
                padding: "6px 12px",
                borderRadius: "8px",
                fontSize: "clamp(10px, 2.5vw, 11px)",
                border: "none",
                minWidth: "130px",
                fontFamily,
              }}
            >
              Prepare Message
            </button>
          </div>
        </div>

        {/* TABLE CONTAINER */}
        <div
          style={{
            overflowX: 'auto',
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: "1px solid #dee2e6",
            marginTop: "1rem",
            marginBottom: "1rem",
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: `${bodyFontSize}px`,
              fontFamily,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                <th className="p-2" style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === sortedUsers.length && sortedUsers.length > 0}
                    onChange={handleSelectAll}
                    style={{
                      cursor: 'pointer',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      border: `2px solid ${theme.colors.textWhite}`,
                      backgroundColor: selectedUsers.length === sortedUsers.length && sortedUsers.length > 0 ? '#ffffff' : 'transparent',
                      appearance: 'none',
                    }}
                  />
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    ID
                    {sortField === 'id' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Name
                    {sortField === 'name' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('role')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Role
                    {sortField === 'role' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('reseller_since')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Start Date
                    {sortField === 'reseller_since' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('city')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    City
                    {sortField === 'city' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('state')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    State
                    {sortField === 'state' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('primary_contact')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Primary Contact
                    {sortField === 'primary_contact' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center p-3"
                    style={{ fontFamily }}
                  >
                    {apiError || 'No users found.'}
                  </td>
                </tr>
              ) : (
                sortedUsers.map((user) => (
                  <tr
                    key={user.id}
                    style={{ borderBottom: "1px solid #dee2e6" }}
                  >
                    <td className="p-2" style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                        style={{
                          cursor: 'pointer',
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          border: `2px solid #999`,
                          backgroundColor: selectedUsers.includes(user.id) ? theme.colors.primary : 'transparent',
                          appearance: 'none',
                        }}
                      />
                    </td>
                    <td className="p-2">{user.id}</td>
                    <td className="p-2">{user.name}</td>
                    <td className="p-2">{user.role}</td>
                    <td className="p-2">
                      {user.reseller_since
                        ? new Date(user.reseller_since).toLocaleDateString()
                        : ''}
                    </td>
                    <td className="p-2">{user.city || 'N/A'}</td>
                    <td className="p-2">{user.state || 'N/A'}</td>
                    <td className="p-2">{user.primary_contact || ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* POPUP MODAL */}
        {showPopup && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "1.5rem",
                width: "100%",
                maxWidth: "500px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                fontFamily,
              }}
            >
              <h5
                style={{
                  fontSize: `${titleFontSize}px`,
                  fontWeight: 'bold',
                  marginBottom: '1rem',
                  color: theme.colors.textPrimary,
                }}
              >
                Prepare Message
              </h5>

              {errors.users && (
                <div style={{ color: theme.colors.danger, marginBottom: '1rem', fontSize: `${bodyFontSize}px` }}>
                  {errors.users}
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Subject*
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: `1px solid ${errors.subject ? theme.colors.danger : '#dee2e6'}`,
                    fontSize: `${bodyFontSize}px`,
                    backgroundColor: '#fff',
                    color: theme.colors.textPrimary,
                  }}
                />
                {errors.subject && (
                  <span style={{ color: theme.colors.danger, fontSize: `${bodyFontSize - 2}px`, display: 'block', marginTop: '0.25rem' }}>
                    {errors.subject}
                  </span>
                )}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Message*
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: `1px solid ${errors.message ? theme.colors.danger : '#dee2e6'}`,
                    minHeight: '120px',
                    resize: 'vertical',
                    fontSize: `${bodyFontSize}px`,
                    backgroundColor: '#fff',
                    color: theme.colors.textPrimary,
                  }}
                />
                {errors.message && (
                  <span style={{ color: theme.colors.danger, fontSize: `${bodyFontSize - 2}px`, display: 'block', marginTop: '0.25rem' }}>
                    {errors.message}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button
                  onClick={handleCancelPopup}
                  style={{
                    backgroundColor: theme.colors.secondary,
                    color: theme.colors.textWhite,
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "none",
                    fontSize: `${bodyFontSize}px`,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={isSending}
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.textWhite,
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "none",
                    fontSize: `${bodyFontSize}px`,
                    opacity: isSending ? 0.7 : 1,
                    cursor: isSending ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {isSending ? (
                    <>
                      Sending...
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #fff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }}
                      />
                    </>
                  ) : (
                    'Send'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Spinner Animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Notify;